import * as Sentry from '@sentry/react'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { I18nextProvider } from 'react-i18next'
import { queryClient, QueryProvider } from '@/app/providers/query'
import { routeTree } from '@/app/route-tree'
import i18next from '@/app/translate'
import { useTranslationJobsStore } from '@/entities/localized-speech/store/translation-jobs-store'
import { useTranslationJobsBeforeUnload } from '@/shared/hooks/use-translation-jobs-before-unload'
import {
  registerAuthContextAccessors,
  registerAuthSync,
  registerTokenAccessor,
} from '@/shared/libs/get-auth-token'
import { useAuthStore } from '@/shared/store/auth-store'
import { ErrorBoundary } from '@/shared/ui/error-boundary'
import { Toaster } from '@/shared/ui/sonner'

registerTokenAccessor(() => useAuthStore.getState().token)
registerAuthSync(
  () => useAuthStore.getState().user,
  (user, token) => useAuthStore.getState().login(user, token),
  () => {
    useTranslationJobsStore.getState().clearAllJobs()
    void queryClient.cancelQueries()
    queryClient.clear()
    useAuthStore.getState().logout()
  },
)
registerAuthContextAccessors(
  () => {
    const id = useAuthStore.getState().activeTenantId?.trim()
    return id === undefined || id.length === 0 ? null : id
  },
  () => useAuthStore.getState().languageCode,
)

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

// Register things for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Wrap router with Sentry's profiler
const SentryRouterProvider = Sentry.withProfiler(RouterProvider)

export function App() {
  useTranslationJobsBeforeUnload()

  return (
    <ErrorBoundary>
      <QueryProvider>
        <I18nextProvider i18n={i18next}>
          <SentryRouterProvider router={router} />
          <Toaster position="top-right" richColors />
        </I18nextProvider>
      </QueryProvider>
    </ErrorBoundary>
  )
}
